import type {
  DraftWaypoint, EditorHeightMode, HeadingParam, MissionConfig, TurnParam, WaylineDraft, WaypointAction, WaypointActionType,
} from '../types/wayline-editor'
import type { WaylineXmlDocument } from './wayline-kmz'
import { ellipsoidToMeanSeaLevel, meanSeaLevelToEllipsoid } from './geoid'
import {
  createDraft, createVertex, createWaypoint, defaultHeadingParam, defaultTurnParam, findDroneProfile, findPayloadProfile,
  generateMappingRoute, nextEditorId, polygonCentroid, round, DRONE_PROFILES,
} from './wayline-editor'

// Serialises a WaylineDraft into DJI WPML (wpmz/template.kml + wpmz/waylines.wpml)
// and packs both into a STORED zip so the cloud import endpoint, DJI Pilot 2 and
// the dock can read it. It also reads such a KMZ back into a draft for editing.
//
// The draft keeps every absolute height as a WGS84 ellipsoid height, the frame
// the aircraft, the dock OSD and the 3D preview all use. template.kml is what
// 司空 2 / Pilot 2 re-edit, and they read it as EGM96 altitudes, so the fields
// documented as 海拔高 are converted through the geoid on the way out and back:
// loadGeoidModel() must have resolved before building or parsing such a route.

const KML_NAMESPACE = 'http://www.opengis.net/kml/2.2'
const WPML_NAMESPACE = 'http://www.dji.com/wpmz/1.0.6'
export const TEMPLATE_ENTRY = 'wpmz/template.kml'
export const WAYLINES_ENTRY = 'wpmz/waylines.wpml'

const ACTUATOR_FUNC: Partial<Record<WaypointActionType, string>> = {
  takePhoto: 'takePhoto',
  startRecord: 'startRecord',
  stopRecord: 'stopRecord',
  hover: 'hover',
  gimbalRotate: 'gimbalRotate',
  rotateYaw: 'rotateYaw',
  zoom: 'zoom',
  focus: 'focus',
  customDirName: 'customDirName',
  panoShot: 'panoShot',
  orientedShoot: 'orientedShoot',
}

// ---------------------------------------------------------------------------
// XML helpers

export function escapeXml (value: string | number | boolean): string {
  return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;')
}

function tag (name: string, value: string | number | boolean): string {
  return `<wpml:${name}>${escapeXml(value)}</wpml:${name}>`
}

function bool (value: boolean): number { return value ? 1 : 0 }
function num (value: number, digits = 6): string { return Number.isFinite(value) ? String(round(value, digits)) : '0' }
function coordinate (point: { longitude: number; latitude: number }): string {
  return `${point.longitude.toFixed(8)},${point.latitude.toFixed(8)}`
}

function documentStart (): string {
  return `<?xml version="1.0" encoding="UTF-8"?><kml xmlns="${KML_NAMESPACE}" xmlns:wpml="${WPML_NAMESPACE}"><Document>`
}

// ---------------------------------------------------------------------------
// Height datum

interface GeoPoint { latitude: number; longitude: number }

/** The point whose undulation stands for the whole route: takeoff, else start. */
function routeDatumPoint (draft: WaylineDraft): GeoPoint | null {
  const takeoff = draft.missionConfig.takeOffRefPoint
  if (takeoff) return takeoff
  const first = draft.templateType === 'mapping2d' ? draft.mapping.vertices[0] : draft.waypoint.waypoints[0]
  return first || null
}

/** EGM96 altitude for a template field DJI documents as 海拔高. */
function egm96Height (point: GeoPoint | null, ellipsoidHeight: number): number {
  const altitude = point ? ellipsoidToMeanSeaLevel(point.latitude, point.longitude, ellipsoidHeight) : null
  if (altitude === null) throw new Error('EGM96 高程基准数据未就绪，无法写出海拔高航线，请稍后重试。')
  return altitude
}

/** A template height: EGM96 above mean sea level, or the draft's own relative height. */
function templateHeight (mode: EditorHeightMode, point: GeoPoint | null, height: number): number {
  return mode === 'WGS84' ? egm96Height(point, height) : height
}

// ---------------------------------------------------------------------------
// Mission configuration

function missionConfigXml (draft: WaylineDraft, executable: boolean): string {
  const config = draft.missionConfig
  const drone = findDroneProfile(draft.droneModelKey) || DRONE_PROFILES[0]
  const payload = findPayloadProfile(draft.droneModelKey, draft.payloadModelKey) || drone.payloads[0]
  const parts = [
    tag('flyToWaylineMode', config.flyToWaylineMode),
    tag('finishAction', config.finishAction),
    tag('exitOnRCLost', config.exitOnRCLost),
    tag('executeRCLostAction', config.executeRCLostAction),
    tag('takeOffSecurityHeight', num(config.takeOffSecurityHeight)),
    tag('globalTransitionalSpeed', num(config.globalTransitionalSpeed)),
    tag('globalRTHHeight', num(config.globalRTHHeight)),
  ]
  if (!executable && config.takeOffRefPoint) {
    const point = config.takeOffRefPoint
    parts.push(tag('takeOffRefPoint', `${point.latitude.toFixed(8)},${point.longitude.toFixed(8)},${num(point.ellipsoidHeight ?? 0)}`))
    // takeOffRefPoint carries the ellipsoid height; takeOffRefPointAGLHeight is
    // how far that point stands above the ground under it, not an altitude.
    // FlightHub 2's own exports write 0.68 m beside a 2045 m pad and place an
    // imported takeoff point that many metres above their terrain, so writing
    // the sea-level altitude here (≈59 m for a 62 m pad) floated the route.
    const agl = point.aglHeight
    parts.push(tag('takeOffRefPointAGLHeight', num(typeof agl === 'number' && Number.isFinite(agl) ? agl : 0, 3)))
  }
  parts.push(
    `<wpml:droneInfo>${tag('droneEnumValue', drone.droneEnumValue)}${tag('droneSubEnumValue', drone.droneSubEnumValue)}</wpml:droneInfo>`,
    `<wpml:payloadInfo>${tag('payloadEnumValue', payload.payloadEnumValue)}${tag('payloadSubEnumValue', payload.payloadSubEnumValue)}${tag('payloadPositionIndex', draft.payloadPositionIndex)}</wpml:payloadInfo>`,
  )
  return `<wpml:missionConfig>${parts.join('')}</wpml:missionConfig>`
}

function templateHeightMode (mode: EditorHeightMode): string {
  return mode === 'WGS84' ? 'EGM96' : 'relativeToStartPoint'
}

function headingParamXml (name: string, param: HeadingParam): string {
  const poi = param.waypointPoiPoint
  return `<wpml:${name}>` +
    tag('waypointHeadingMode', param.waypointHeadingMode) +
    tag('waypointHeadingAngle', num(param.waypointHeadingAngle, 2)) +
    tag('waypointPoiPoint', poi ? `${poi[1].toFixed(8)},${poi[0].toFixed(8)},0.000000` : '0.000000,0.000000,0.000000') +
    tag('waypointHeadingAngleEnable', bool(param.waypointHeadingMode === 'smoothTransition')) +
    tag('waypointHeadingPathMode', param.waypointHeadingPathMode) +
    `</wpml:${name}>`
}

function turnParamXml (param: TurnParam): string {
  const damping = param.waypointTurnMode === 'coordinateTurn' || (param.waypointTurnMode === 'toPointAndPassWithContinuityCurvature' && param.useStraightLine)
    ? Math.max(0.2, param.waypointTurnDampingDist)
    : 0
  return '<wpml:waypointTurnParam>' + tag('waypointTurnMode', param.waypointTurnMode) + tag('waypointTurnDampingDist', num(damping, 2)) + '</wpml:waypointTurnParam>'
}

// ---------------------------------------------------------------------------
// Actions

interface ActionGroupXml {
  startIndex: number
  endIndex: number
  trigger: { type: string; param?: number }
  actions: string[]
}

function actionXml (draft: WaylineDraft, action: WaypointAction, actionId: number): string | null {
  const func = ACTUATOR_FUNC[action.type]
  if (!func) return null
  const p = action.params
  const position = tag('payloadPositionIndex', draft.payloadPositionIndex)
  let params = ''
  switch (action.type) {
    case 'takePhoto':
    case 'startRecord':
      params = position + tag('fileSuffix', String(p.fileSuffix ?? '')) + tag('payloadLensIndex', String(p.payloadLensIndex ?? 'wide')) + tag('useGlobalPayloadLensIndex', bool(Boolean(p.useGlobalPayloadLensIndex)))
      break
    case 'stopRecord':
      params = position + tag('payloadLensIndex', String(p.payloadLensIndex ?? 'wide'))
      break
    case 'hover':
      params = tag('hoverTime', num(Number(p.hoverTime) || 1, 1))
      break
    case 'gimbalRotate':
      params = position +
        tag('gimbalHeadingYawBase', 'north') +
        tag('gimbalRotateMode', 'absoluteAngle') +
        tag('gimbalPitchRotateEnable', bool(Boolean(p.gimbalPitchRotateEnable))) +
        tag('gimbalPitchRotateAngle', num(Number(p.gimbalPitchRotateAngle) || 0, 1)) +
        tag('gimbalRollRotateEnable', 0) +
        tag('gimbalRollRotateAngle', 0) +
        tag('gimbalYawRotateEnable', bool(Boolean(p.gimbalYawRotateEnable))) +
        tag('gimbalYawRotateAngle', num(Number(p.gimbalYawRotateAngle) || 0, 1)) +
        tag('gimbalRotateTimeEnable', bool(Boolean(p.gimbalRotateTimeEnable))) +
        tag('gimbalRotateTime', num(Number(p.gimbalRotateTime) || 0, 1))
      break
    case 'rotateYaw':
      params = tag('aircraftHeading', num(Number(p.aircraftHeading) || 0, 1)) + tag('aircraftPathMode', String(p.aircraftPathMode || 'clockwise'))
      break
    case 'zoom':
      params = position + tag('focalLength', num(Number(p.focalLength) || 24, 1))
      break
    case 'focus':
      params = position +
        tag('isPointFocus', bool(Boolean(p.isPointFocus))) +
        tag('focusX', num(Number(p.focusX) || 0, 3)) +
        tag('focusY', num(Number(p.focusY) || 0, 3)) +
        tag('focusRegionWidth', num(Number(p.focusRegionWidth) || 0, 3)) +
        tag('focusRegionHeight', num(Number(p.focusRegionHeight) || 0, 3)) +
        tag('isInfiniteFocus', bool(Boolean(p.isInfiniteFocus)))
      break
    case 'customDirName':
      params = position + tag('directoryName', String(p.directoryName || 'folder'))
      break
    case 'panoShot':
      params = position + tag('panoShotSubMode', String(p.panoShotSubMode || 'panoShot_360')) + tag('payloadLensIndex', String(p.payloadLensIndex ?? 'wide')) + tag('useGlobalPayloadLensIndex', bool(Boolean(p.useGlobalPayloadLensIndex)))
      break
    case 'orientedShoot': {
      const payload = findPayloadProfile(draft.droneModelKey, draft.payloadModelKey)
      params =
        tag('gimbalPitchRotateAngle', num(Number(p.gimbalPitchRotateAngle) || 0, 1)) +
        tag('gimbalYawRotateAngle', num(Number(p.gimbalYawRotateAngle) || 0, 1)) +
        tag('focusX', 480) + tag('focusY', 360) + tag('focusRegionWidth', 960) + tag('focusRegionHeight', 720) +
        tag('focalLength', num(Number(p.focalLength) || 24, 1)) +
        tag('aircraftHeading', num(Number(p.aircraftHeading) || 0, 1)) +
        tag('accurateFrameValid', 0) +
        position +
        tag('payloadLensIndex', String(p.payloadLensIndex ?? 'wide')) +
        tag('useGlobalPayloadLensIndex', bool(Boolean(p.useGlobalPayloadLensIndex))) +
        tag('targetAngle', 0) +
        tag('actionUUID', String(p.actionUUID || action.id)) +
        tag('imageWidth', 960) + tag('imageHeight', 720) + tag('AFPos', 0) + tag('gimbalPort', 0) +
        tag('orientedCameraType', payload?.payloadEnumValue ?? 0) +
        tag('orientedFilePath', '') + tag('orientedFileMD5', '') + tag('orientedFileSize', 0) +
        tag('orientedFileSuffix', String(p.orientedFileSuffix ?? '')) +
        tag('orientedCameraApertue', 0) + tag('orientedCameraLuminance', 0) + tag('orientedCameraShutterTime', 0) + tag('orientedCameraISO', 0) +
        tag('orientedPhotoMode', String(p.orientedPhotoMode || 'normalPhoto'))
      break
    }
  }
  return `<wpml:action>${tag('actionId', actionId)}${tag('actionActuatorFunc', func)}<wpml:actionActuatorFuncParam>${params}</wpml:actionActuatorFuncParam></wpml:action>`
}

/** Collect the action groups of a waypoint route: reach-point groups plus interval shot spans. */
function collectActionGroups (draft: WaylineDraft, waypoints: DraftWaypoint[]): Map<number, ActionGroupXml[]> {
  const groups = new Map<number, ActionGroupXml[]>()
  const push = (index: number, group: ActionGroupXml) => {
    if (!groups.has(index)) groups.set(index, [])
    // Interval (between-waypoint) groups lead the waypoint, as FlightHub 2 orders them.
    if (group.trigger.type === 'reachPoint') groups.get(index)!.push(group)
    else groups.get(index)!.unshift(group)
  }
  type IntervalSpan = { start: number; type: 'multipleTiming' | 'multipleDistance'; param: number; lens: string }
  let interval: IntervalSpan | null = null
  waypoints.forEach((waypoint, index) => {
    const actions: string[] = []
    waypoint.actions.forEach(action => {
      if (action.type === 'startTimedShot' || action.type === 'startDistanceShot') {
        if (interval) push(interval.start, intervalGroup(draft, interval, index))
        interval = {
          start: index,
          type: action.type === 'startTimedShot' ? 'multipleTiming' : 'multipleDistance',
          param: Math.max(0.1, Number(action.params.interval) || 1),
          lens: String(action.params.payloadLensIndex ?? 'wide'),
        }
        return
      }
      if (action.type === 'endIntervalShot') {
        if (interval) push(interval.start, intervalGroup(draft, interval, index))
        interval = null
        return
      }
      const xml = actionXml(draft, action, actions.length)
      if (xml) actions.push(xml)
    })
    if (actions.length) push(index, { startIndex: index, endIndex: index, trigger: { type: 'reachPoint' }, actions })
  })
  const open = interval as IntervalSpan | null
  if (open) push(open.start, intervalGroup(draft, open, waypoints.length - 1))
  return groups
}

function intervalGroup (draft: WaylineDraft, interval: { start: number; type: string; param: number; lens: string }, endIndex: number): ActionGroupXml {
  const action: WaypointAction = { id: 'interval', type: 'takePhoto', params: { fileSuffix: '', payloadLensIndex: interval.lens, useGlobalPayloadLensIndex: false } }
  return { startIndex: interval.start, endIndex: Math.max(endIndex, interval.start), trigger: { type: interval.type, param: interval.param }, actions: [actionXml(draft, action, 0)!] }
}

function actionGroupsXml (groups: ActionGroupXml[], firstGroupId: { value: number }): string {
  return groups.map(group => {
    const id = firstGroupId.value++
    const trigger = tag('actionTriggerType', group.trigger.type) + (group.trigger.param !== undefined ? tag('actionTriggerParam', num(group.trigger.param, 2)) : '')
    return `<wpml:actionGroup>${tag('actionGroupId', id)}${tag('actionGroupStartIndex', group.startIndex)}${tag('actionGroupEndIndex', group.endIndex)}${tag('actionGroupMode', 'sequence')}<wpml:actionTrigger>${trigger}</wpml:actionTrigger>${group.actions.join('')}</wpml:actionGroup>`
  }).join('')
}

// ---------------------------------------------------------------------------
// Waypoint template

function waypointHeights (draft: WaylineDraft, waypoint: DraftWaypoint): { ellipsoid: number; height: number; execute: number } {
  const template = draft.waypoint
  const height = waypoint.useGlobalHeight ? template.globalHeight : waypoint.height
  // In 椭球高 mode the draft height already is the ellipsoid height the aircraft
  // flies; only the editable template field is converted, by the caller.
  // In relative mode WPML defines wpml:ellipsoidHeight as the same number as
  // wpml:height (dji-wpml/template-kml: "如果 wpml:height 选用相对起飞点高度，则
  // wpml:ellipsoidHeight 和 wpml:height 相同"). Writing pad + height there made
  // FlightHub 2 lift the whole route by the pad's ellipsoid height.
  return { ellipsoid: height, height, execute: height }
}

function waypointTemplateXml (draft: WaylineDraft): string {
  const template = draft.waypoint
  const groups = collectActionGroups(draft, template.waypoints)
  const groupId = { value: 0 }
  const placemarks = template.waypoints.map((waypoint, index) => {
    const heights = waypointHeights(draft, waypoint)
    return '<Placemark>' +
      `<Point><coordinates>${coordinate(waypoint)}</coordinates></Point>` +
      tag('index', index) +
      tag('ellipsoidHeight', num(heights.ellipsoid, 3)) +
      tag('height', num(templateHeight(template.heightMode, waypoint, heights.height), 3)) +
      tag('useGlobalHeight', bool(waypoint.useGlobalHeight)) +
      tag('useGlobalSpeed', bool(waypoint.useGlobalSpeed)) +
      (waypoint.useGlobalSpeed ? '' : tag('waypointSpeed', num(waypoint.speed, 2))) +
      tag('useGlobalHeadingParam', bool(waypoint.useGlobalHeadingParam)) +
      (waypoint.useGlobalHeadingParam ? '' : headingParamXml('waypointHeadingParam', waypoint.headingParam)) +
      tag('useGlobalTurnParam', bool(waypoint.useGlobalTurnParam)) +
      (waypoint.useGlobalTurnParam ? '' : turnParamXml(waypoint.turnParam)) +
      tag('useStraightLine', bool(waypoint.useGlobalTurnParam ? template.globalUseStraightLine : waypoint.turnParam.useStraightLine)) +
      tag('gimbalPitchAngle', num(waypoint.gimbalPitchAngle, 1)) +
      tag('isRisky', bool(waypoint.isRisky)) +
      actionGroupsXml(groups.get(index) || [], groupId) +
      '</Placemark>'
  })
  return '<Folder>' +
    tag('templateType', 'waypoint') +
    tag('templateId', 0) +
    '<wpml:waylineCoordinateSysParam>' + tag('coordinateMode', 'WGS84') + tag('heightMode', templateHeightMode(template.heightMode)) + tag('positioningType', 'GPS') + '</wpml:waylineCoordinateSysParam>' +
    tag('autoFlightSpeed', num(template.autoFlightSpeed, 2)) +
    tag('globalHeight', num(templateHeight(template.heightMode, routeDatumPoint(draft), template.globalHeight), 3)) +
    tag('caliFlightEnable', 0) +
    tag('gimbalPitchMode', template.gimbalPitchMode) +
    headingParamXml('globalWaypointHeadingParam', template.globalHeadingParam) +
    tag('globalWaypointTurnMode', template.globalWaypointTurnMode) +
    tag('globalUseStraightLine', bool(template.globalUseStraightLine)) +
    '<wpml:payloadParam>' + tag('payloadPositionIndex', draft.payloadPositionIndex) + tag('imageFormat', template.imageFormat.join(',') || 'wide') + '</wpml:payloadParam>' +
    placemarks.join('') +
    '</Folder>'
}

function waypointWaylinesXml (draft: WaylineDraft): string {
  const template = draft.waypoint
  const groups = collectActionGroups(draft, template.waypoints)
  const groupId = { value: 0 }
  const placemarks = template.waypoints.map((waypoint, index) => {
    const heights = waypointHeights(draft, waypoint)
    const heading = waypoint.useGlobalHeadingParam ? template.globalHeadingParam : waypoint.headingParam
    const turn: TurnParam = waypoint.useGlobalTurnParam
      ? { waypointTurnMode: template.globalWaypointTurnMode, waypointTurnDampingDist: waypoint.turnParam.waypointTurnDampingDist, useStraightLine: template.globalUseStraightLine }
      : waypoint.turnParam
    return '<Placemark>' +
      `<Point><coordinates>${coordinate(waypoint)}</coordinates></Point>` +
      tag('index', index) +
      tag('executeHeight', num(template.heightMode === 'WGS84' ? heights.ellipsoid : heights.execute, 3)) +
      tag('waypointSpeed', num(waypoint.useGlobalSpeed ? template.autoFlightSpeed : waypoint.speed, 2)) +
      headingParamXml('waypointHeadingParam', heading) +
      turnParamXml(turn) +
      tag('useStraightLine', bool(turn.useStraightLine)) +
      tag('isRisky', bool(waypoint.isRisky)) +
      actionGroupsXml(groups.get(index) || [], groupId) +
      '</Placemark>'
  })
  return '<Folder>' +
    tag('templateId', 0) +
    tag('executeHeightMode', template.heightMode === 'WGS84' ? 'WGS84' : 'relativeToStartPoint') +
    tag('waylineId', 0) +
    tag('autoFlightSpeed', num(template.autoFlightSpeed, 2)) +
    placemarks.join('') +
    '</Folder>'
}

// ---------------------------------------------------------------------------
// 2D mapping template

function mappingTemplateXml (draft: WaylineDraft): string {
  const mapping = draft.mapping
  // As with waypoints, wpml:ellipsoidHeight is the draft height itself: the
  // ellipsoid height in 椭球高 mode, the relative height otherwise.
  const ellipsoid = mapping.height
  // As in the waypoint template, the editable height is an EGM96 altitude.
  const shootHeight = templateHeight(mapping.heightMode, routeDatumPoint(draft), mapping.height)
  const ring = [...mapping.vertices, mapping.vertices[0]].filter(Boolean).map(vertex => `${coordinate(vertex)},0`).join(' ')
  return '<Folder>' +
    tag('templateType', 'mapping2d') +
    tag('templateId', 0) +
    tag('autoFlightSpeed', num(mapping.autoFlightSpeed, 2)) +
    '<wpml:waylineCoordinateSysParam>' +
      tag('coordinateMode', 'WGS84') +
      tag('heightMode', templateHeightMode(mapping.heightMode)) +
      tag('positioningType', 'GPS') +
      tag('globalShootHeight', num(shootHeight, 3)) +
      tag('surfaceFollowModeEnable', 0) +
      tag('surfaceRelativeHeight', num(shootHeight, 3)) +
    '</wpml:waylineCoordinateSysParam>' +
    '<wpml:payloadParam>' + tag('payloadPositionIndex', draft.payloadPositionIndex) + tag('focusMode', 'firstPoint') + tag('meteringMode', 'average') + tag('dewarpingEnable', 0) + tag('imageFormat', mapping.imageFormat.join(',') || 'wide') + '</wpml:payloadParam>' +
    '<Placemark>' +
      `<Polygon><outerBoundaryIs><LinearRing><coordinates>${ring}</coordinates></LinearRing></outerBoundaryIs></Polygon>` +
      tag('caliFlightEnable', 0) +
      tag('elevationOptimizeEnable', bool(mapping.elevationOptimizeEnable)) +
      tag('smartObliqueEnable', 0) +
      tag('shootType', mapping.shootType) +
      tag('direction', Math.round(mapping.direction)) +
      tag('margin', Math.round(mapping.margin)) +
      '<wpml:overlap>' + tag('orthoCameraOverlapH', Math.round(mapping.orthoCameraOverlapH)) + tag('orthoCameraOverlapW', Math.round(mapping.orthoCameraOverlapW)) + '</wpml:overlap>' +
      tag('ellipsoidHeight', num(ellipsoid, 3)) +
      tag('height', num(shootHeight, 3)) +
      '<wpml:mappingHeadingParam>' + tag('mappingHeadingMode', mapping.mappingHeadingMode) + tag('mappingHeadingAngle', Math.round(mapping.mappingHeadingAngle)) + '</wpml:mappingHeadingParam>' +
      tag('gimbalPitchMode', mapping.gimbalPitchMode) +
      tag('gimbalPitchAngle', Math.round(mapping.gimbalPitchAngle)) +
    '</Placemark>' +
    '</Folder>'
}

function mappingWaylinesXml (draft: WaylineDraft): string {
  const mapping = draft.mapping
  const generated = generateMappingRoute(draft)
  if (!generated) throw new Error('无法从当前测绘区域生成航线。')
  const executeHeight = mapping.height
  const heading: HeadingParam = mapping.mappingHeadingMode === 'fixed'
    ? { waypointHeadingMode: 'smoothTransition', waypointHeadingAngle: normalizeHeading(mapping.mappingHeadingAngle), waypointPoiPoint: null, waypointHeadingPathMode: 'followBadArc' }
    : { waypointHeadingMode: 'followWayline', waypointHeadingAngle: 0, waypointPoiPoint: null, waypointHeadingPathMode: 'followBadArc' }
  const turn: TurnParam = { waypointTurnMode: 'toPointAndStopWithDiscontinuityCurvature', waypointTurnDampingDist: 0, useStraightLine: true }
  const points = [...generated.waypoints]
  const groupId = { value: 0 }
  const pitch = mapping.gimbalPitchMode === 'fixed' ? mapping.gimbalPitchAngle : -90
  const lens = mapping.imageFormat.join(',') || 'wide'
  const gimbal: WaypointAction = { id: 'gimbal', type: 'gimbalRotate', params: { gimbalPitchRotateEnable: true, gimbalPitchRotateAngle: pitch, gimbalYawRotateEnable: false, gimbalYawRotateAngle: 0, gimbalRotateTimeEnable: false, gimbalRotateTime: 0 } }
  const photo: WaypointAction = { id: 'photo', type: 'takePhoto', params: { fileSuffix: '', payloadLensIndex: lens, useGlobalPayloadLensIndex: false } }
  const groups = new Map<number, ActionGroupXml[]>()
  groups.set(0, [
    { startIndex: 0, endIndex: 0, trigger: { type: 'reachPoint' }, actions: [actionXml(draft, gimbal, 0)!] },
    { startIndex: 0, endIndex: points.length - 1, trigger: { type: mapping.shootType === 'time' ? 'multipleTiming' : 'multipleDistance', param: generated.triggerParam }, actions: [actionXml(draft, photo, 0)!] },
  ])
  if (mapping.elevationOptimizeEnable) {
    // Fly to the survey centre and capture a ring of oblique photos.
    const centre = polygonCentroid(mapping.vertices)
    points.push(centre)
    const actions: string[] = []
    const obliqueGimbal: WaypointAction = { ...gimbal, params: { ...gimbal.params, gimbalPitchRotateAngle: -45 } }
    actions.push(actionXml(draft, obliqueGimbal, actions.length)!)
    ;[0, 90, 180, -90].forEach(angle => {
      actions.push(actionXml(draft, { id: `yaw${angle}`, type: 'rotateYaw', params: { aircraftHeading: angle, aircraftPathMode: 'clockwise' } }, actions.length)!)
      actions.push(actionXml(draft, photo, actions.length)!)
    })
    groups.set(points.length - 1, [{ startIndex: points.length - 1, endIndex: points.length - 1, trigger: { type: 'reachPoint' }, actions }])
  }
  const placemarks = points.map((point, index) => '<Placemark>' +
    `<Point><coordinates>${coordinate(point)}</coordinates></Point>` +
    tag('index', index) +
    tag('executeHeight', num(executeHeight, 3)) +
    tag('waypointSpeed', num(mapping.autoFlightSpeed, 2)) +
    headingParamXml('waypointHeadingParam', heading) +
    turnParamXml(turn) +
    tag('useStraightLine', 1) +
    actionGroupsXml(groups.get(index) || [], groupId) +
    '</Placemark>')
  return '<Folder>' +
    tag('templateId', 0) +
    tag('executeHeightMode', mapping.heightMode === 'WGS84' ? 'WGS84' : 'relativeToStartPoint') +
    tag('waylineId', 0) +
    tag('autoFlightSpeed', num(mapping.autoFlightSpeed, 2)) +
    placemarks.join('') +
    '</Folder>'
}

function normalizeHeading (angle: number): number {
  const wrapped = ((angle % 360) + 540) % 360 - 180
  return wrapped === -180 ? 180 : wrapped
}

// ---------------------------------------------------------------------------
// Public builders

export function buildTemplateKml (draft: WaylineDraft): string {
  return documentStart() +
    tag('author', draft.author || 'dji-cloud-web') +
    tag('createTime', draft.createTime) +
    tag('updateTime', draft.updateTime) +
    missionConfigXml(draft, false) +
    (draft.templateType === 'mapping2d' ? mappingTemplateXml(draft) : waypointTemplateXml(draft)) +
    '</Document></kml>'
}

export function buildWaylinesWpml (draft: WaylineDraft): string {
  return documentStart() +
    missionConfigXml(draft, true) +
    (draft.templateType === 'mapping2d' ? mappingWaylinesXml(draft) : waypointWaylinesXml(draft)) +
    '</Document></kml>'
}

export function buildWaylineDocuments (draft: WaylineDraft): WaylineXmlDocument[] {
  return [
    { name: WAYLINES_ENTRY, xml: buildWaylinesWpml(draft) },
    { name: TEMPLATE_ENTRY, xml: buildTemplateKml(draft) },
  ]
}

export function buildKmzBytes (draft: WaylineDraft): Uint8Array {
  return buildStoredZip([
    { name: TEMPLATE_ENTRY, data: new TextEncoder().encode(buildTemplateKml(draft)) },
    { name: WAYLINES_ENTRY, data: new TextEncoder().encode(buildWaylinesWpml(draft)) },
  ])
}

export function buildKmzFile (draft: WaylineDraft): File {
  const bytes = buildKmzBytes(draft)
  return new File([bytes], `${draft.name.trim()}.kmz`, { type: 'application/zip' })
}

// ---------------------------------------------------------------------------
// Minimal STORED zip writer (no compression; the import service inflates either)

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[n] = c >>> 0
  }
  return table
})()

export function crc32 (data: Uint8Array): number {
  let crc = 0xffffffff
  for (let index = 0; index < data.length; index++) crc = CRC_TABLE[(crc ^ data[index]) & 0xff] ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

function dosDateTime (date: Date): { time: number; date: number } {
  const time = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2)
  const day = ((Math.max(1980, date.getFullYear()) - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate()
  return { time, date: day }
}

export function buildStoredZip (entries: Array<{ name: string; data: Uint8Array }>, now = new Date()): Uint8Array {
  const encoder = new TextEncoder()
  const stamp = dosDateTime(now)
  const locals: Uint8Array[] = []
  const centrals: Uint8Array[] = []
  let offset = 0
  entries.forEach(entry => {
    const name = encoder.encode(entry.name)
    const crc = crc32(entry.data)
    const local = new DataView(new ArrayBuffer(30))
    local.setUint32(0, 0x04034b50, true)
    local.setUint16(4, 20, true)
    local.setUint16(6, 0x0800, true)
    local.setUint16(8, 0, true)
    local.setUint16(10, stamp.time, true)
    local.setUint16(12, stamp.date, true)
    local.setUint32(14, crc, true)
    local.setUint32(18, entry.data.length, true)
    local.setUint32(22, entry.data.length, true)
    local.setUint16(26, name.length, true)
    local.setUint16(28, 0, true)
    const central = new DataView(new ArrayBuffer(46))
    central.setUint32(0, 0x02014b50, true)
    central.setUint16(4, 20, true)
    central.setUint16(6, 20, true)
    central.setUint16(8, 0x0800, true)
    central.setUint16(10, 0, true)
    central.setUint16(12, stamp.time, true)
    central.setUint16(14, stamp.date, true)
    central.setUint32(16, crc, true)
    central.setUint32(20, entry.data.length, true)
    central.setUint32(24, entry.data.length, true)
    central.setUint16(28, name.length, true)
    central.setUint16(30, 0, true)
    central.setUint16(32, 0, true)
    central.setUint16(34, 0, true)
    central.setUint16(36, 0, true)
    central.setUint32(38, 0, true)
    central.setUint32(42, offset, true)
    locals.push(new Uint8Array(local.buffer), name, entry.data)
    centrals.push(new Uint8Array(central.buffer), name)
    offset += 30 + name.length + entry.data.length
  })
  const centralSize = centrals.reduce((total, part) => total + part.length, 0)
  const end = new DataView(new ArrayBuffer(22))
  end.setUint32(0, 0x06054b50, true)
  end.setUint16(4, 0, true)
  end.setUint16(6, 0, true)
  end.setUint16(8, entries.length, true)
  end.setUint16(10, entries.length, true)
  end.setUint32(12, centralSize, true)
  end.setUint32(16, offset, true)
  end.setUint16(20, 0, true)
  const parts = [...locals, ...centrals, new Uint8Array(end.buffer)]
  const output = new Uint8Array(parts.reduce((total, part) => total + part.length, 0))
  let cursor = 0
  parts.forEach(part => { output.set(part, cursor); cursor += part.length })
  return output
}

// ---------------------------------------------------------------------------
// Reading a KMZ back into an editable draft

export interface DraftLoadResult {
  draft: WaylineDraft
  /** Elements the editor does not model and would drop on save. */
  warnings: string[]
}

function localName (element: Element): string {
  return (element.localName || element.nodeName.split(':').pop() || '').toLowerCase()
}

function children (root: Element | Document, name: string): Element[] {
  const target = name.toLowerCase()
  return Array.from(root.getElementsByTagName('*')).filter(element => localName(element) === target)
}

function elementChildren (root: Element): Element[] {
  // childNodes rather than children: the node test harness uses xmldom.
  return Array.from(root.childNodes).filter((node): node is Element => node.nodeType === 1)
}

function directChild (root: Element, name: string): Element | null {
  const target = name.toLowerCase()
  return elementChildren(root).find(element => localName(element) === target) || null
}

function text (root: Element | Document | null, name: string, fallback = ''): string {
  if (!root) return fallback
  const element = (root as Element).nodeType === 9 ? children(root, name)[0] : directChild(root as Element, name) || children(root, name)[0]
  return (element?.textContent || fallback).trim()
}

function numberOf (root: Element | Document | null, name: string, fallback: number): number {
  const value = Number(text(root, name, ''))
  return Number.isFinite(value) && text(root, name, '') !== '' ? value : fallback
}

/** Longitude/latitude of the first placemark, the route datum when no takeoff point is set. */
function firstPlacemarkPoint (folder: Element): GeoPoint | null {
  const placemark = children(folder, 'Placemark')[0]
  if (!placemark) return null
  // A waypoint holds one coordinate, a mapping polygon a whitespace separated ring.
  const first = text(placemark, 'coordinates', '').trim().split(/\s+/)[0] || ''
  const values = first.split(',').map(value => Number(value.trim()))
  return Number.isFinite(values[0]) && Number.isFinite(values[1]) ? { latitude: values[1], longitude: values[0] } : null
}

function parseHeadingParam (element: Element | null, fallback: HeadingParam): HeadingParam {
  if (!element) return { ...fallback }
  const poiText = text(element, 'waypointPoiPoint', '')
  const poi = poiText.split(',').map(value => Number(value.trim()))
  const hasPoi = poi.length >= 2 && Number.isFinite(poi[0]) && Number.isFinite(poi[1]) && (poi[0] !== 0 || poi[1] !== 0)
  return {
    waypointHeadingMode: (text(element, 'waypointHeadingMode', fallback.waypointHeadingMode) as HeadingParam['waypointHeadingMode']),
    waypointHeadingAngle: numberOf(element, 'waypointHeadingAngle', 0),
    waypointPoiPoint: hasPoi ? [poi[1], poi[0]] : null,
    waypointHeadingPathMode: (text(element, 'waypointHeadingPathMode', 'followBadArc') as HeadingParam['waypointHeadingPathMode']),
  }
}

function parseTurnParam (element: Element | null, straight: boolean, fallback: TurnParam): TurnParam {
  if (!element) return { ...fallback, useStraightLine: straight }
  return {
    waypointTurnMode: text(element, 'waypointTurnMode', fallback.waypointTurnMode) as TurnParam['waypointTurnMode'],
    waypointTurnDampingDist: numberOf(element, 'waypointTurnDampingDist', 0),
    useStraightLine: straight,
  }
}

function parseActionGroups (placemark: Element, index: number, waypoints: DraftWaypoint[], warnings: string[]): void {
  const waypoint = waypoints[index]
  children(placemark, 'actionGroup').forEach(group => {
    const triggerType = text(group, 'actionTriggerType', 'reachPoint')
    const startIndex = numberOf(group, 'actionGroupStartIndex', index)
    const endIndex = numberOf(group, 'actionGroupEndIndex', index)
    if (triggerType === 'multipleTiming' || triggerType === 'multipleDistance') {
      const lens = text(group, 'payloadLensIndex', 'wide')
      const start = waypoints[startIndex] || waypoint
      start.actions.push({ id: nextEditorId('action'), type: triggerType === 'multipleTiming' ? 'startTimedShot' : 'startDistanceShot', params: { interval: numberOf(group, 'actionTriggerParam', 2), payloadLensIndex: lens } })
      const end = waypoints[endIndex]
      if (end && end !== start) end.actions.push({ id: nextEditorId('action'), type: 'endIntervalShot', params: {} })
      return
    }
    if (triggerType !== 'reachPoint') {
      warnings.push(`航点 ${index + 1} 的触发方式 ${triggerType} 不受编辑器支持，已忽略。`)
      return
    }
    children(group, 'action').forEach(actionElement => {
      const func = text(actionElement, 'actionActuatorFunc', '')
      const type = (Object.keys(ACTUATOR_FUNC) as WaypointActionType[]).find(key => ACTUATOR_FUNC[key] === func)
      if (!type) {
        warnings.push(`航点 ${index + 1} 的动作 ${func || '(未知)'} 不受编辑器支持，已忽略。`)
        return
      }
      const paramElement = directChild(actionElement, 'actionActuatorFuncParam')
      const params: Record<string, string | number | boolean> = {}
      if (paramElement) {
        elementChildren(paramElement).forEach(child => {
          const key = child.localName || child.nodeName.split(':').pop() || ''
          const value = (child.textContent || '').trim()
          if (/^(gimbal\w+Enable|useGlobalPayloadLensIndex|isPointFocus|isInfiniteFocus)$/.test(key)) params[key] = value === '1'
          else if (value !== '' && Number.isFinite(Number(value)) && !/^(fileSuffix|directoryName|payloadLensIndex|actionUUID|orientedFileSuffix)$/.test(key)) params[key] = Number(value)
          else params[key] = value
        })
      }
      waypoint.actions.push({ id: nextEditorId('action'), type, params })
    })
  })
}

/** Load a template.kml (plus optional waylines.wpml) authored by this editor or DJI software. */
export function parseWaylineDraftDocuments (documents: WaylineXmlDocument[], name: string, author: string): DraftLoadResult {
  const warnings: string[] = []
  const templateDocument = documents.find(item => item.name.toLowerCase().endsWith('.kml'))
  if (!templateDocument) throw new Error('航线文件缺少 template.kml，无法编辑。')
  const template = new DOMParser().parseFromString(templateDocument.xml, 'application/xml')
  if (children(template, 'parsererror').length) throw new Error('template.kml 解析失败。')
  const folder = children(template, 'Folder')[0]
  if (!folder) throw new Error('template.kml 缺少航线模板。')
  const templateType = text(folder, 'templateType', 'waypoint')
  if (templateType !== 'waypoint' && templateType !== 'mapping2d') {
    throw new Error(`编辑器暂不支持 ${templateType} 类型的航线。`)
  }
  const mission = children(template, 'missionConfig')[0]
  const droneInfo = mission ? directChild(mission, 'droneInfo') : null
  const payloadInfo = mission ? directChild(mission, 'payloadInfo') : null
  const droneKey = `0-${numberOf(droneInfo, 'droneEnumValue', 67)}-${numberOf(droneInfo, 'droneSubEnumValue', 0)}`
  const droneProfile = findDroneProfile(droneKey)
  if (!droneProfile) throw new Error('该航线的飞行器型号不受编辑器支持。')
  const payloadKey = `1-${numberOf(payloadInfo, 'payloadEnumValue', droneProfile.payloads[0].payloadEnumValue)}-${numberOf(payloadInfo, 'payloadSubEnumValue', 0)}`
  const draft = createDraft({ name, templateType, droneModelKey: droneKey, payloadModelKey: payloadKey, author })
  draft.payloadPositionIndex = numberOf(payloadInfo, 'payloadPositionIndex', 0)
  draft.createTime = numberOf(template, 'createTime', draft.createTime)
  const config: MissionConfig = draft.missionConfig
  config.flyToWaylineMode = text(mission, 'flyToWaylineMode', config.flyToWaylineMode) as MissionConfig['flyToWaylineMode']
  config.finishAction = text(mission, 'finishAction', config.finishAction) as MissionConfig['finishAction']
  config.exitOnRCLost = text(mission, 'exitOnRCLost', config.exitOnRCLost) as MissionConfig['exitOnRCLost']
  config.executeRCLostAction = text(mission, 'executeRCLostAction', config.executeRCLostAction) as MissionConfig['executeRCLostAction']
  config.takeOffSecurityHeight = numberOf(mission, 'takeOffSecurityHeight', config.takeOffSecurityHeight)
  config.globalTransitionalSpeed = numberOf(mission, 'globalTransitionalSpeed', config.globalTransitionalSpeed)
  config.globalRTHHeight = numberOf(mission, 'globalRTHHeight', config.globalRTHHeight)
  const takeoffText = text(mission, 'takeOffRefPoint', '')
  if (takeoffText) {
    const values = takeoffText.split(',').map(value => Number(value.trim()))
    if (Number.isFinite(values[0]) && Number.isFinite(values[1])) {
      const agl = numberOf(mission, 'takeOffRefPointAGLHeight', NaN)
      config.takeOffRefPoint = { latitude: values[0], longitude: values[1], ellipsoidHeight: Number.isFinite(values[2]) ? values[2] : null, aglHeight: Number.isFinite(agl) ? agl : null }
    }
  }
  const coordinateParam = children(folder, 'waylineCoordinateSysParam')[0] || null
  const heightModeText = text(coordinateParam, 'heightMode', 'relativeToStartPoint')
  const heightMode: EditorHeightMode = heightModeText === 'relativeToStartPoint' ? 'relativeToStartPoint' : 'WGS84'
  if (heightModeText !== 'relativeToStartPoint' && heightModeText !== 'EGM96' && heightModeText !== 'WGS84') {
    warnings.push(`高度模式 ${heightModeText} 将按椭球高编辑。`)
  }
  const payloadParam = children(folder, 'payloadParam')[0] || null
  const imageFormat = text(payloadParam, 'imageFormat', '').split(',').map(value => value.trim()).filter(Boolean) as WaylineDraft['waypoint']['imageFormat']
  // Under EGM96 the template heights are 海拔高, while the draft edits ellipsoid
  // heights. wpml:ellipsoidHeight is used whenever the file carries it; the rest
  // converts at the route datum point, matching what the writer emits.
  const datumPoint = config.takeOffRefPoint || firstPlacemarkPoint(folder)
  let warnedGeoid = false
  const fromEgm96 = (height: number, point: GeoPoint | null = datumPoint): number => {
    if (heightModeText !== 'EGM96') return height
    const converted = point ? meanSeaLevelToEllipsoid(point.latitude, point.longitude, height) : null
    if (converted !== null) return converted
    if (!warnedGeoid) {
      warnedGeoid = true
      warnings.push('EGM96 高程基准数据未就绪，海拔高暂按椭球高读取，高度可能相差数十米。')
    }
    return height
  }

  if (templateType === 'waypoint') {
    const wp = draft.waypoint
    wp.heightMode = heightMode
    wp.autoFlightSpeed = numberOf(folder, 'autoFlightSpeed', wp.autoFlightSpeed)
    wp.globalHeight = fromEgm96(numberOf(folder, 'globalHeight', wp.globalHeight))
    wp.globalWaypointTurnMode = text(folder, 'globalWaypointTurnMode', wp.globalWaypointTurnMode) as TurnParam['waypointTurnMode']
    wp.globalUseStraightLine = text(folder, 'globalUseStraightLine', '1') === '1'
    wp.gimbalPitchMode = text(folder, 'gimbalPitchMode', wp.gimbalPitchMode) === 'usePointSetting' ? 'usePointSetting' : 'manual'
    wp.globalHeadingParam = parseHeadingParam(children(folder, 'globalWaypointHeadingParam')[0] || null, defaultHeadingParam())
    if (imageFormat.length) wp.imageFormat = imageFormat
    const placemarks = children(folder, 'Placemark')
    placemarks.forEach(placemark => {
      const coordinates = text(placemark, 'coordinates', '').split(',').map(value => Number(value.trim()))
      if (!Number.isFinite(coordinates[0]) || !Number.isFinite(coordinates[1])) return
      const useGlobalHeight = text(placemark, 'useGlobalHeight', '0') === '1'
      const point: GeoPoint = { latitude: coordinates[1], longitude: coordinates[0] }
      const ellipsoid = numberOf(placemark, 'ellipsoidHeight', NaN)
      const height = heightMode === 'WGS84'
        ? (Number.isFinite(ellipsoid) ? ellipsoid : fromEgm96(numberOf(placemark, 'height', wp.globalHeight), point))
        : numberOf(placemark, 'height', wp.globalHeight)
      const waypoint = createWaypoint(draft, coordinates[0], coordinates[1], useGlobalHeight ? wp.globalHeight : height)
      waypoint.useGlobalHeight = useGlobalHeight
      waypoint.useGlobalSpeed = text(placemark, 'useGlobalSpeed', '1') === '1'
      waypoint.speed = numberOf(placemark, 'waypointSpeed', wp.autoFlightSpeed)
      waypoint.useGlobalHeadingParam = text(placemark, 'useGlobalHeadingParam', '1') === '1'
      waypoint.headingParam = parseHeadingParam(children(placemark, 'waypointHeadingParam')[0] || null, wp.globalHeadingParam)
      waypoint.useGlobalTurnParam = text(placemark, 'useGlobalTurnParam', '1') === '1'
      const straight = text(placemark, 'useStraightLine', wp.globalUseStraightLine ? '1' : '0') === '1'
      waypoint.turnParam = parseTurnParam(children(placemark, 'waypointTurnParam')[0] || null, straight, defaultTurnParam())
      waypoint.gimbalPitchAngle = numberOf(placemark, 'gimbalPitchAngle', waypoint.gimbalPitchAngle)
      waypoint.isRisky = text(placemark, 'isRisky', '0') === '1'
      wp.waypoints.push(waypoint)
    })
    placemarks.forEach((placemark, index) => { if (wp.waypoints[index]) parseActionGroups(placemark, index, wp.waypoints, warnings) })
  } else {
    const mapping = draft.mapping
    mapping.heightMode = heightMode
    mapping.autoFlightSpeed = numberOf(folder, 'autoFlightSpeed', mapping.autoFlightSpeed)
    const placemark = children(folder, 'Placemark')[0] || null
    const ringText = text(placemark, 'coordinates', '')
    const vertices = ringText.split(/\s+/).map(item => item.split(',').map(value => Number(value.trim()))).filter(values => Number.isFinite(values[0]) && Number.isFinite(values[1]))
    if (vertices.length > 1 && vertices[0][0] === vertices[vertices.length - 1][0] && vertices[0][1] === vertices[vertices.length - 1][1]) vertices.pop()
    mapping.vertices = vertices.map(values => createVertex(values[0], values[1]))
    const ellipsoid = numberOf(placemark, 'ellipsoidHeight', NaN)
    mapping.height = heightMode === 'WGS84'
      ? (Number.isFinite(ellipsoid) ? ellipsoid : fromEgm96(numberOf(placemark, 'height', mapping.height)))
      : numberOf(placemark, 'height', mapping.height)
    mapping.direction = numberOf(placemark, 'direction', 0)
    mapping.margin = numberOf(placemark, 'margin', 0)
    mapping.orthoCameraOverlapH = numberOf(placemark, 'orthoCameraOverlapH', mapping.orthoCameraOverlapH)
    mapping.orthoCameraOverlapW = numberOf(placemark, 'orthoCameraOverlapW', mapping.orthoCameraOverlapW)
    mapping.shootType = text(placemark, 'shootType', 'time') === 'distance' ? 'distance' : 'time'
    mapping.elevationOptimizeEnable = text(placemark, 'elevationOptimizeEnable', '0') === '1'
    mapping.gimbalPitchMode = text(placemark, 'gimbalPitchMode', 'manual') === 'fixed' ? 'fixed' : 'manual'
    mapping.gimbalPitchAngle = numberOf(placemark, 'gimbalPitchAngle', -90)
    mapping.mappingHeadingMode = text(placemark, 'mappingHeadingMode', 'followWayline') === 'fixed' ? 'fixed' : 'followWayline'
    mapping.mappingHeadingAngle = numberOf(placemark, 'mappingHeadingAngle', 0)
    if (imageFormat.length) { mapping.imageFormat = imageFormat; mapping.lens = imageFormat[0] }
    if (text(placemark, 'smartObliqueEnable', '0') === '1') warnings.push('智能摆拍设置不受编辑器支持，保存后将关闭。')
  }
  return { draft, warnings }
}
